import { Button, Card, CardActions, CardContent, Skeleton, Stack, Typography, useMediaQuery } from '@mui/material'
import Grid from '@mui/material/Grid'
import {
  CampaignLocation,
  CompanyShopTemplatesDocument,
  useCompanyShopTemplatesQuery,
  useDeleteCompanyShopTemplateMutation,
} from '~/generated/graphql'
import { useCallback, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { formatPhoneNumber } from '~/lib/phoneNumber'
import { getPrefectureNameJpByPref } from '~/lib/prefectureHelper'
import { ErrorAlert } from '~/components/common/ErrorAlert'
import { ToggleViewButton } from '~/components/common/ToggleButton'
import { theme } from '~/lib/theme'
import ClearIcon from '@mui/icons-material/Clear'
import { storeInfoSchema as schema } from '~/features/campaign_input/StoreInfo'
import { z } from 'zod'
import { decodeId } from '~/lib/convertId'
import { SimpleDialog } from '~/components/common/SimpleDialog'
import { formatPostalCode } from '~/lib/postalCode'

export const StoreTemplateList: React.FC = () => {
  // formProviderを使用して、親コンポーネントのフォームと連携する
  const { setValue, clearErrors, watch } = useFormContext<Omit<z.input<typeof schema>, 'category'>>()
  const shopName = watch('shopName')
  const shopTemplateId = watch('shopTemplateId')
  const setShop = (shop: Omit<z.infer<typeof schema>, 'category'>) => {
    setValue('shopName', shop.shopName, { shouldDirty: true })
    setValue('shopPostalCode', shop.shopPostalCode, { shouldDirty: true })
    setValue('shopPref', shop.shopPref, { shouldDirty: true })
    setValue('cityName', shop.cityName, { shouldDirty: true })
    setValue('streetAddress', shop.streetAddress, { shouldDirty: true })
    setValue('buildingName', shop.buildingName, { shouldDirty: true })
    setValue('nearestStationName', shop.nearestStationName, { shouldDirty: true })
    setValue('shopTel', shop.shopTel, { shouldDirty: true })
    setValue('shopTemplateId', shop.shopTemplateId, { shouldDirty: true })
    clearErrors(['shopName', 'shopPostalCode', 'shopPref', 'cityName', 'streetAddress'])
  }

  const { data, loading, error } = useCompanyShopTemplatesQuery({
    fetchPolicy: 'cache-and-network',

    onCompleted: (data) => {
      const selectedShop = data.viewer.companyShopTemplates[0]
      if (!shopName && selectedShop && shopTemplateId === null) {
        setShop({
          shopName: selectedShop.name || '',
          shopPostalCode: selectedShop.postalCode || '',
          shopPref: selectedShop.prefectureCode || null,
          cityName: selectedShop.cityName || '',
          streetAddress: selectedShop.streetAddress || '',
          buildingName: selectedShop.buildingName ?? null,
          nearestStationName: selectedShop.stationName || '',
          shopTel: selectedShop.tel || '',
          shopTemplateId: decodeId(selectedShop.id).id,
        })
      }
    },
  })

  const companyShopTemplates = data?.viewer.companyShopTemplates

  const handleAutoFillShopInfo = (value: string) => {
    const id = value
    const selectedShop = companyShopTemplates?.find((shop) => shop.id === id)
    if (!selectedShop) return
    setShop({
      shopName: selectedShop.name || '',
      shopPostalCode: selectedShop.postalCode || '',
      shopPref: selectedShop.prefectureCode || null,
      cityName: selectedShop.cityName || '',
      streetAddress: selectedShop.streetAddress || '',
      buildingName: selectedShop.buildingName ?? null,
      nearestStationName: selectedShop.stationName || '',
      shopTel: selectedShop.tel || '',
      shopTemplateId: decodeId(selectedShop.id).id,
    })
  }

  return (
    <>
      {error && <ErrorAlert title="店舗テンプレートの取得に失敗しました" error={error} />}

      <Stack direction="row" my={2}>
        <Grid container spacing={2} width="100%">
          {loading ? (
            <>
              <Grid size={{ xs: 12, md: 6 }}>
                <Skeleton variant="rectangular" width="100%" height={150} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Skeleton variant="rectangular" width="100%" height={150} />
              </Grid>
            </>
          ) : (
            <StoreCardList
              companyShopTemplates={companyShopTemplates}
              handleAutoFillShopInfo={handleAutoFillShopInfo}
            />
          )}
        </Grid>
      </Stack>
    </>
  )
}

type StoreCardProps = {
  companyShopTemplates?: readonly (Pick<
    CampaignLocation,
    | 'id'
    | 'name'
    | 'postalCode'
    | 'prefectureCode'
    | 'cityName'
    | 'cityCode'
    | 'streetAddress'
    | 'buildingName'
    | 'stationName'
    | 'tel'
  > & { __typename?: 'CampaignLocation' })[]
  handleAutoFillShopInfo: (value: string) => void
}
const StoreCardList: React.FC<StoreCardProps> = ({ companyShopTemplates, handleAutoFillShopInfo }) => {
  const [deleteShop] = useDeleteCompanyShopTemplateMutation()
  const isBreakpointUpSm = useMediaQuery(theme.breakpoints.up('sm'))
  const [showMore, setMoreView] = useState(false)
  const [dialogOpenShopTemplateId, setDialogOpenShopTemplateId] = useState<string | null>(null)

  const handleClick = useCallback(() => {
    setMoreView((prev) => !prev)
    if (showMore) {
      window.scrollTo(0, 0)
    }
  }, [showMore])

  const visibleCompanyTemplates = useMemo(
    () =>
      showMore
        ? companyShopTemplates
        : isBreakpointUpSm
          ? companyShopTemplates?.slice(0, VIEW_TOGGLE_THRESHOLD_PC)
          : companyShopTemplates?.slice(0, VIEW_TOGGLE_THRESHOLD_MOBILE),
    [companyShopTemplates, showMore, isBreakpointUpSm],
  )

  return (
    <Grid container spacing={2} py={2} width="100%">
      {visibleCompanyTemplates?.map((shop) => (
        <>
          <Grid
            key={shop.id}
            size={{ xs: 12, sm: 6 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Card
              elevation={5}
              sx={{
                position: 'relative',
                p: 1,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Button
                color="error"
                onClick={() => setDialogOpenShopTemplateId(shop.id)}
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 2,
                }}
              >
                <ClearIcon fontSize="small" />
              </Button>
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack spacing={0.5}>
                  <Typography pr={5}>店舗名：{shop.name}</Typography>
                  <Typography variant="caption">
                    住所：〒{formatPostalCode(shop.postalCode)}
                    {shop.prefectureCode && getPrefectureNameJpByPref(shop.prefectureCode)}
                    {shop.cityName}
                    {shop.streetAddress}
                    {shop.buildingName}
                  </Typography>
                  <Typography variant="caption">最寄り駅：{shop.stationName}</Typography>
                  <Typography variant="caption">電話番号：{shop.tel ? formatPhoneNumber(shop.tel) : ''}</Typography>
                </Stack>
              </CardContent>

              <CardActions
                sx={{
                  justifyContent: 'center',
                  pb: 2,
                }}
              >
                <Button onClick={() => handleAutoFillShopInfo(shop.id)} variant="contained" size="small" fullWidth>
                  店舗情報を自動入力
                </Button>
              </CardActions>
            </Card>
          </Grid>
          <SimpleDialog
            open={dialogOpenShopTemplateId === shop.id}
            onOk={() => {
              deleteShop({
                variables: {
                  input: {
                    id: shop.id,
                  },
                },
                refetchQueries: [CompanyShopTemplatesDocument],
              })
              setDialogOpenShopTemplateId(null)
            }}
            onCancel={() => {
              setDialogOpenShopTemplateId(null)
            }}
            onClose={() => {
              setDialogOpenShopTemplateId(null)
            }}
            title="店舗情報の削除"
            okButtonText="削除"
            closeButtonText="閉じる"
          >
            <Typography variant="body2">本当に削除しますか？</Typography>
          </SimpleDialog>
        </>
      ))}
      <ToggleViewButton
        length={companyShopTemplates?.length ?? 0}
        handleClick={handleClick}
        isMoreView={showMore}
        toggleButtonThreshold={isBreakpointUpSm ? VIEW_TOGGLE_THRESHOLD_PC : VIEW_TOGGLE_THRESHOLD_MOBILE}
      />
    </Grid>
  )
}

const VIEW_TOGGLE_THRESHOLD_PC = 2
const VIEW_TOGGLE_THRESHOLD_MOBILE = 1
